/**
 * Journey screen. The map is the primary view on a large display; on a phone
 * the ordered route list leads and the map is opt-in, because a pinch-zoom
 * minimap is not an operational tool on a small screen.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { useLive } from '../hooks/useLive';
import { JourneyCanvas } from '../components/JourneyCanvas';
import { JourneyList } from '../components/JourneyList';
import { JourneyInspector } from '../components/JourneyInspector';
import { FreshnessTag, Panel, StateChip } from '../components/Primitives';
import { DegradedBanner, ErrorState, LiveBadge, SkeletonPanel } from '../components/States';
import { absoluteTime, relativeTime } from '../lib/format';
import { useAuth } from '../lib/auth';
import { roleAllows } from '@dcc/shared';
import { useIsPhone } from '../hooks/useMediaQuery';

export function JourneyScreen() {
  const { projectId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const asOf = params.get('asOf');
  const routeFilter = params.get('route');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusCritical, setFocusCritical] = useState(false);
  const [showMapOnMobile, setShowMapOnMobile] = useState(false);
  const { user } = useAuth();
  const isPhone = useIsPhone();

  const projection = useProjection(() => api.journey(projectId, asOf), [projectId, asOf]);
  const live = useLive(['journey.updated', 'project.updated'], projection.reload);

  useEffect(() => {
    if (!projection.data || selectedId) return;
    setSelectedId(
      projection.data.currentPositionNodeIds[0] ?? projection.data.nodes[0]?.id ?? null,
    );
  }, [projection.data, selectedId]);

  if (projection.loading) return <SkeletonPanel rows={6} />;
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const journey = projection.data!;
  const selected = journey.nodes.find((node) => node.id === selectedId) ?? null;
  const canApprove = user ? roleAllows(user.role, 'station:approve') : false;

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">
            <Link to={`/projects/${projectId}/control`}>{journey.projectName}</Link> ·{' '}
            {journey.versionKey ?? 'no version'}
          </div>
          <h1 className="topstrip__title">Journey</h1>
        </div>
        <div className="topstrip__meta">
          <span className="chip" data-tone="active">
            {journey.completionPercent}% complete
          </span>
          <LiveBadge connection={live.connection} lastEventAt={live.lastEventAt} />
          <FreshnessTag freshness={journey.freshness} />
        </div>
      </header>

      {projection.degraded && (
        <DegradedBanner onRetry={projection.reload} loadedAt={projection.loadedAt} />
      )}

      {asOf && (
        <div className="banner" data-tone="attention" role="status" aria-label="History mode">
          <span className="chip__glyph" aria-hidden="true">
            !
          </span>
          <span className="banner__text">
            History mode: showing the route as it stood at {absoluteTime(asOf)}, reconstructed from
            the event log. This is not the current state.
          </span>
          <button type="button" className="btn btn--sm" onClick={() => setParam('asOf', null)}>
            Back to now
          </button>
        </div>
      )}

      <Panel
        title="Route controls"
        subtitle="Filter to one route, focus the critical path, or travel back in time."
      >
        <div className="inline">
          <label className="inline inline--tight small">
            <span className="label">Route</span>
            <select
              className="btn btn--sm"
              value={routeFilter ?? ''}
              onChange={(event) => setParam('route', event.target.value || null)}
            >
              <option value="">All routes</option>
              {journey.routes.map((route) => (
                <option key={route.key} value={route.key}>
                  {route.label} ({route.nodeCount})
                </option>
              ))}
            </select>
          </label>

          <label className="inline inline--tight small">
            <input
              type="checkbox"
              checked={focusCritical}
              onChange={(event) => setFocusCritical(event.target.checked)}
            />
            Fit the critical path ({journey.criticalPathNodeIds.length} nodes)
          </label>

          <label className="inline inline--tight small">
            <span className="label">History</span>
            <input
              className="btn btn--sm"
              type="datetime-local"
              onChange={(event) =>
                setParam(
                  'asOf',
                  event.target.value ? new Date(event.target.value).toISOString() : null,
                )
              }
            />
          </label>

          <button
            type="button"
            className="btn btn--sm btn--quiet"
            onClick={() => setSelectedId(journey.currentPositionNodeIds[0] ?? null)}
            disabled={journey.currentPositionNodeIds.length === 0}
          >
            Go to current position
          </button>
        </div>
      </Panel>

      {isPhone ? (
        /* Phone: the ordered route list leads, because a pinch-zoom minimap is
           not an operational tool on a small screen. The map is opt-in. */
        <div className="stack">
          <div className="inline">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setShowMapOnMobile((value) => !value)}
              aria-pressed={showMapOnMobile}
            >
              {showMapOnMobile ? 'Show route list' : 'Show route map'}
            </button>
          </div>
          {showMapOnMobile ? (
            <JourneyCanvas
              journey={journey}
              selectedId={selectedId}
              onSelect={setSelectedId}
              routeFilter={routeFilter}
              focusCriticalPath={focusCritical}
            />
          ) : (
            <JourneyList
              journey={journey}
              selectedId={selectedId}
              onSelect={setSelectedId}
              routeFilter={routeFilter}
            />
          )}
          <JourneyInspector
            node={selected}
            journey={journey}
            projectId={projectId}
            canApprove={canApprove}
            onChanged={projection.reload}
          />
        </div>
      ) : (
        <div className="journey__split">
          <JourneyCanvas
            journey={journey}
            selectedId={selectedId}
            onSelect={setSelectedId}
            routeFilter={routeFilter}
            focusCriticalPath={focusCritical}
          />
          <JourneyInspector
            node={selected}
            journey={journey}
            projectId={projectId}
            canApprove={canApprove}
            onChanged={projection.reload}
          />
        </div>
      )}

      {/* The accessible equivalent of the map. On a phone the list above already
          is the primary view, so it is not repeated here. */}
      {!isPhone && (
        <Panel
          title="Route steps"
          subtitle="The same journey as an ordered list, with dependencies stated in words."
        >
          <JourneyList
            journey={journey}
            selectedId={selectedId}
            onSelect={setSelectedId}
            routeFilter={routeFilter}
          />
        </Panel>
      )}

      <Panel title="Stations" subtitle="Where the journey becomes testable by a person.">
        <div className="grid grid--3">
          {journey.nodes
            .filter((node) => node.type === 'TESTABLE_STATION' || node.type === 'DESTINATION')
            .map((node) => (
              <button
                key={node.id}
                type="button"
                className="card card--interactive card--toned"
                data-tone={
                  node.status === 'DONE'
                    ? 'success'
                    : node.status === 'BLOCKED'
                      ? 'critical'
                      : 'neutral'
                }
                onClick={() => setSelectedId(node.id)}
              >
                <div className="spread">
                  <strong>{node.title}</strong>
                  <StateChip kind="workItem" state={node.status} />
                </div>
                <p className="small dim" style={{ marginTop: 6 }}>
                  {node.station?.whatIsTestable ?? node.subtitle ?? 'No station payload recorded.'}
                </p>
                <p className="tiny dim" style={{ marginTop: 6 }}>
                  {node.etaAt ? `ETA ${relativeTime(node.etaAt)}` : 'no ETA derived'}
                </p>
              </button>
            ))}
        </div>
      </Panel>
    </>
  );
}
